const Logger = require('./Logger');
const Levels = require('./Levels');
const ConsoleLogDestination = require('./destination/ConsoleLog');

const DEFAULT_COMPONENT = '-default-';

/**
 * Log is the entry point to the logging system.
 * @classdesc
 */
class Log {
  /**
   * Creates a new logging system.
   *
   * Loggers can be configured for specific components, and new destinations can be
   * added for the log events to be sent to.
   *
   * By default it is configured to output log events at 'info' level and above, to a
   * console output destination.  Use 'configure' to change these.
   */
  constructor() {
    this.loggers = null;
    this.config = null;
    this.defaultLevel = null;
    this.rootLogger = null;
    this.destinations = [];
    this.clear();
  }

  /**
   * Gets a new logger with methods for outputting log information.
   *
   * An optional component can be configured which can then have its log level controlled.
   *
   * @param {string} component the component that the new logger is logging for.
   * @return {Logger} A logger for the specified component.
   */
  getLogger(component = DEFAULT_COMPONENT) {
    if (this.loggers[component] !== undefined) {
      return this.loggers[component];
    }
    const logger = new Logger(this, component);
    const level = bestLevelMatch(this.config, component, this.defaultLevel);
    logger.setLevel(level);
    this.loggers[component] = logger;

    return logger;
  }

  /**
   * Configure the various log levels and destinations.
   * @param {[*]} args defaultLevel, config, destinations[]
   * @return {undefined}
   */
  configure(...args) {
    let [defaultLevel, config, destinations] = args;
    if (args.length === 1 && typeof defaultLevel === 'object') {
      config = args[0];
      defaultLevel = 'info';
    }
    config = config || {};
    this.config = config;
    this.defaultLevel = defaultLevel;
    setLoggerLevels(defaultLevel, config, this.loggers);
    this.rootLogger = this.getLogger();

    this.clearDestinations();

    if (!destinations && typeof console !== 'undefined') {
      destinations = [new ConsoleLogDestination()];
    }
    destinations = destinations || [];

    for (let i = 0; i < destinations.length; ++i) {
      this.addDestination(destinations[i]);
    }
  }

  /**
   * Configures just the logging levels of the components.
   * @param {[*]} args either (component, level), or (newDefaultLevel)
   * @returns {undefined}
   */
  changeLevel(...args) {
    if (args.length === 1) {
      this.defaultLevel = args[0];
    } else {
      const [component, level] = args;
      this.config[component] = level;
    }
    setLoggerLevels(this.defaultLevel, this.config, this.loggers);
    this.rootLogger = this.getLogger();
  }

  /**
   * Adds an output destination.
   *
   * @param {function} logDestinationArg either an object with an onLog method or a function that
   *                      will be executed whenever there is a log event of appropriate level.
   * @param {object} contextArg the object that should be 'this' for the onLog function.
   *                      May be null or undefined.
   * @returns {undefined}
   */
  addDestination(logDestinationArg, contextArg) {
    let logDestination = logDestinationArg;
    let context = contextArg;
    if (context === undefined && typeof logDestination !== 'function') {
      context = logDestination;
      logDestination = context.onLog;
    }
    this.destinations.push({ destination: logDestination, context });
  }

  /**
   * Removes the oldest output destination matching its arguments.
   * Should be called with the same arguments as addDestination.
   *
   * @param {function} logDestinationArg see the addDestination function.
   * @param {object} contextArg see the addDestination function.
   *
   * @returns {undefined}
   */
  removeDestination(logDestinationArg, contextArg) {
    let logDestination = logDestinationArg;
    let context = contextArg;
    if (context === undefined && typeof logDestination !== 'function') {
      context = logDestination;
      logDestination = context.onLog;
    }
    for (let i = 0; i < this.destinations.length; ++i) {
      const { destination: testDest, context: testContext } = this.destinations[i];
      if (testDest === logDestination && testContext === context) {
        this.destinations.splice(i, 1);
        break;
      }
    }
  }

  /**
   * Removes all destinations.
   *
   * @returns {undefined}
   */
  clearDestinations() {
    this.destinations.length = 0;
  }

  /**
   * Resets all component loggers and destinations.  Resets the log level to info.
   *
   * @returns {undefined}
   */
  clear() {
    this.loggers = {};
    this.configure('info');
  }

  onLog(component, level, logData, time) {
    for (const { destination, context } of this.destinations) {
      destination.call(context, component, level, logData, time);
    }
  }
}

Log.prototype.DEFAULT_COMPONENT = DEFAULT_COMPONENT;

function bestLevelMatch(config, key, otherwise) {
  const candidates = Object.keys(config)
    .filter((a) => a === key || (key.substring(0, a.length + 1) === `${a}.`))
    .sort((a, b) => b.length - a.length);
  return candidates[0] ? config[candidates[0]] : otherwise;
}

function setLoggerLevels(defaultLevel, config, loggers) {
  for (const loggerId of Object.keys(loggers)) {
    const logger = loggers[loggerId];
    const level = bestLevelMatch(config, loggerId, defaultLevel);
    logger.setLevel(level);
  }
}

Log.prototype.Levels = Levels;

Levels.forEach((level) => {
  Log.prototype[level.toUpperCase()] = level;

  // Convenience methods to log to the root logger.
  Log.prototype[level] = function logDelegate(...args) {
    /* eslint prefer-spread:0 */
    this.rootLogger[level].apply(this.rootLogger, args);
  };
});

module.exports = Log;
